import * as React from "react";

import type { AuditLogEntry } from "api/auditLogs";
import { getApiErrorMessage } from "api/apiError";
import { Button, DataTable, EmptyState, ErrorState, Pagination } from "Components/ui";
import { History, RefreshCw } from "Components/ui/icons";
import type { AuditLogsData } from "../types";
import { AuditLogDetailSheet } from "./AuditLogDetailSheet";
import { AuditLogFilters } from "./AuditLogFilters";
import { buildAuditLogColumns } from "./columns";

export interface AuditLogExplorerProps {
  data: AuditLogsData;
  /** Caps the table's height (the header stays put while rows scroll), e.g. "60vh" or 480. */
  maxHeight?: number | string;
  /** Controls at the right end of the filter row, e.g. Export when there's no page header. */
  toolbarActions?: React.ReactNode;
}

/** Filters, table, pager and the detail sheet. The page and the tenant history dialog share it. */
export const AuditLogExplorer: React.FC<AuditLogExplorerProps> = ({
  data,
  maxHeight,
  toolbarActions,
}) => {
  const [selected, setSelected] = React.useState<AuditLogEntry | null>(null);
  const [sheetOpen, setSheetOpen] = React.useState<boolean>(false);

  const openEntry = React.useCallback((entry: AuditLogEntry) => {
    setSelected(entry);
    setSheetOpen(true);
  }, []);

  const columns = React.useMemo(() => buildAuditLogColumns(openEntry), [openEntry]);

  const resolvedMaxHeight = typeof maxHeight === "number" ? `${maxHeight}px` : maxHeight;
  const hasError = data.error !== null && data.error !== undefined;

  const empty = hasError ? (
    <ErrorState
      title="Audit logs didn't load"
      description={getApiErrorMessage(data.error, "Check your connection and try again.")}
      action={
        <>
          <Button variant="secondary" leftIcon={<RefreshCw />} onClick={data.reload}>
            Try again
          </Button>
          <Button variant="ghost" onClick={data.dismissError}>
            Dismiss
          </Button>
        </>
      }
    />
  ) : (
    <EmptyState
      compact
      icon={<History />}
      title={
        data.hasActiveFilters ? "No entries match these filters" : "No activity in the last 30 days"
      }
      description={
        data.hasActiveFilters
          ? "Try a wider date range or clear the filters."
          : "Changes admins make to tenants, users, roles and settings show up here."
      }
      action={
        data.hasActiveFilters ? (
          <Button variant="secondary" onClick={data.clearFilters}>
            Clear filters
          </Button>
        ) : undefined
      }
    />
  );

  const table = (
    <DataTable
      columns={columns}
      data={hasError ? [] : data.logs}
      loading={data.loading}
      getRowId={(row) => row._id}
      onRowClick={openEntry}
      enableSorting={false}
      stickyHeader={Boolean(resolvedMaxHeight)}
      caption="Audit log entries"
      className={resolvedMaxHeight ? "min-h-0" : undefined}
      empty={empty}
      footer={
        !hasError && data.total > 0 ? (
          <Pagination
            page={data.page}
            totalPages={data.totalPages}
            onPageChange={data.setPage}
            totalItems={data.total}
            pageSize={data.pageSize}
            noun="entries"
            disabled={data.loading}
          />
        ) : undefined
      }
    />
  );

  return (
    <div className="space-y-4">
      <AuditLogFilters data={data} trailing={toolbarActions} />
      {resolvedMaxHeight ? (
        // A height-capped column lets the table's own scroller shrink and keeps the pager visible.
        <div className="flex min-h-0 flex-col" style={{ maxHeight: resolvedMaxHeight }}>
          {table}
        </div>
      ) : (
        table
      )}
      <AuditLogDetailSheet entry={selected} open={sheetOpen} onOpenChange={setSheetOpen} />
    </div>
  );
};
